import React, { useState, useEffect } from 'react';
import { TallyEntry, TallyConnectionStatus } from '../../types/tally';
import ExcelUploader from '../../components/tallly/ExcelUploader';
import TallyConnector from '../../components/tallly/TallyConnector';
import SuccessMessage from '../../components/tallly/SuccessMessage';
import { convertToXML } from '../../lib/tally/tally.xml';
import { importVouchersToTally } from '../../lib/tally/tallyExport';

const TallyUploadPage: React.FC = () => {
  const [tallyData, setTallyData] = useState<TallyEntry[]>([]);
  const [tenantName, setTenantName] = useState<string>('');
  const [connectionStatus, setConnectionStatus] =
    useState<TallyConnectionStatus>({
      isConnected: false,
      message: '',
    });
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const handleDataParsed = (data: TallyEntry[]) => {
    console.log('Data parsed in parent component:', data.length, 'entries');
    setTallyData(data);
    setError('');
  };

  useEffect(() => {
    console.log(
      'Button should be ' +
      (connectionStatus.isConnected &&
        tallyData.length > 0 &&
        tenantName.trim()
        ? 'ENABLED'
        : 'DISABLED'),
    );
  }, [connectionStatus.isConnected, tallyData.length, tenantName]);

  const handleConnectionStatusChange = (status: TallyConnectionStatus) => {
    console.log('Connection status changed:', status);
    setConnectionStatus(status);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Submit button clicked');

    if (!connectionStatus.isConnected) {
      setError(
        'Tally connection is not established. Please connect to Tally first.',
      );
      return;
    }

    if (tallyData.length === 0) {
      setError('Please upload an Excel file first.');
      return;
    }

    if (!tenantName.trim()) {
      setError('Please enter a valid company/tenant name.');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // Convert data to Tally XML format
      console.log('Converting data to XML format');
      const tallyXml = convertToXML(tallyData);
      console.log('XML created successfully');

      // Send data to Tally
      console.log('Sending data to Tally');
      const response = await importVouchersToTally(tallyXml);
      console.log('Tally response received:', response);

      if (response.success) {
        setSuccessMessage(
          `Successfully imported ${tallyData.length} records to Tally for company "${tenantName}".`,
        );
        setSuccess(true);
        setTallyData([]);
        setTenantName('');
      } else {
        setError(`Failed to import data: ${response.message}`);
      }
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      setError(
        `Error processing data: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setProcessing(false);
    }
  };

  const closeSuccessMessage = () => {
    setSuccess(false);
  };

  const isFormValid =
    connectionStatus.isConnected &&
    tallyData.length > 0 &&
    tenantName.trim() !== '';

  return (
    <div className="container mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold mb-6">Tally Import Tool</h1>

      <TallyConnector onConnectionStatusChange={handleConnectionStatusChange} />

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Upload Excel Data</h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label
              htmlFor="tenantName"
              className="block text-sm font-medium text-gray-700 mb-1">
              Company Name (as configured in Tally)
            </label>
            <input
              type="text"
              id="tenantName"
              value={tenantName}
              onChange={e => setTenantName(e.target.value)}
              placeholder="Enter the exact company name as in Tally"
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Excel File
            </label>
            <ExcelUploader onDataParsed={handleDataParsed} />
          </div>

          {tallyData.length > 0 && (
            <div className="mb-6">
              <div className="bg-green-50 border border-green-200 rounded-md p-3">
                <p className="text-green-800">
                  <span className="font-medium">File loaded successfully!</span>{' '}
                  Found {tallyData.length} records to process.
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6">
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!isFormValid || processing}
              className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${isFormValid && !processing
                  ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
                  : 'bg-blue-300 text-white cursor-not-allowed'
                }`}>
              {processing ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                'Import to Tally'
              )}
            </button>
          </div>
        </form>
      </div>

      {success && (
        <SuccessMessage
          message={successMessage}
          onClose={closeSuccessMessage}
        />
      )}
    </div>
  );
};

export default TallyUploadPage;
